import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Signup.css";

const Signup = () => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    badge: "",
    fleetNo: "",
    signature: null,
    signatureType: "upload",
  });
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null); // Track userId
  const [lineWidth, setLineWidth] = useState(2);
  const [lineColor, setLineColor] = useState("#000000");
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, signature: e.target.files[0] }));
  };

  const handleSignatureTypeChange = (type) => {
    setFormData((prev) => ({ ...prev, signatureType: type, signature: null }));
    if (type === "draw") {
      setTimeout(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = lineColor;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }, 0);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const dataURLToBlob = (dataURL) => {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const saveCanvasImage = async () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL("image/png");

    if (dataURL === "data:,") {
      setMessage("Please draw a signature before uploading.");
      return;
    }

    const blob = dataURLToBlob(dataURL);
    const file = new File([blob], "signature.png", { type: "image/png" });

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("signature", file);

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature-noauth/${userId}`,
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage("Signature saved and uploaded successfully!");
      setTimeout(() => {
        navigate("/", { state: { username: formData.username } });
      }, 2000);
    } catch (error) {
      console.error("Error uploading signature:", error);
      setMessage("Failed to upload signature.");
    }
  };

  const uploadFileSignature = async () => {
    if (!formData.signature) {
      setMessage("Please select a file before uploading.");
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("signature", formData.signature);

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature-noauth/${userId}`,
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage("Signature file uploaded successfully!");
      setTimeout(() => {
        navigate("/", { state: { username: formData.username } });
      }, 2000);
    } catch (error) {
      console.error("Error uploading signature file:", error);
      setMessage("Failed to upload signature file.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/Signup`,
        {
          ...formData,
          userrole: "driver",
        }
      );

      setUserId(response.data.userId); // Save userId
      setMessage("Signup successful! You can now add your signature.");
      setFormData({
        name: "",
        username: "",
        email: "",
        password: "",
        badge: "",
        fleetNo: "",
        signature: null,
        signatureType: "upload",
      });
    } catch (error) {
      console.error("Error signing up:", error);
      setMessage(
        error.response?.data?.message ||
          "Signup failed. Please check your information and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate("/");
  };

  return (
    <div className="driver-signup-container">
      <button className="close-button" onClick={handleClose} aria-label="Close">
        &times;
      </button>
      <form onSubmit={handleSubmit} className="driver-signup-form">
        <h2 className="driver-signup-title">Driver Registration</h2>

        <div className="driver-signup-field">
          <label className="driver-signup-label">
            Name <span className="driver-signup-required">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="driver-signup-input"
            required
            placeholder="Enter your name"
          />
        </div>

        <div className="driver-signup-field">
          <label className="driver-signup-label">
            Username <span className="driver-signup-required">*</span>
          </label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className="driver-signup-input"
            required
            placeholder="Choose a username"
          />
        </div>

        <div className="driver-signup-field">
          <label className="driver-signup-label">
            Email <span className="driver-signup-required">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="driver-signup-input"
            required
            placeholder="Enter your email"
          />
        </div>

        <div className="driver-signup-field">
          <label className="driver-signup-label">
            Password <span className="driver-signup-required">*</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="driver-signup-input"
            required
            placeholder="Create a password"
          />
        </div>

        <div className="driver-signup-field">
          <label className="driver-signup-label">
            Badge Number <span className="driver-signup-required">*</span>
          </label>
          <input
            type="text"
            name="badge"
            value={formData.badge}
            onChange={handleChange}
            className="driver-signup-input"
            required
            placeholder="Enter badge number"
          />
        </div>

        <div className="driver-signup-field">
          <label className="driver-signup-label">Fleet Number</label>
          <input
            type="text"
            name="fleetNo"
            value={formData.fleetNo}
            onChange={handleChange}
            className="driver-signup-input"
            placeholder="Enter fleet number (optional)"
          />
        </div>

        <button
          type="submit"
          className="driver-signup-submit-btn"
          disabled={isSubmitting || userId !== null}
        >
          {isSubmitting ? "Registering..." : "Register as Driver"}
        </button>

        {message && (
          <div
            className={`driver-signup-message ${
              message.includes("success") ? "success" : "error"
            }`}
          >
            {message}
          </div>
        )}

        <div className="driver-signup-field full-width">
          <label className="driver-signup-label">Signature</label>
          {!userId && (
            <p className="note">
              Please submit your information first. The signature section will
              be enabled afterward.
            </p>
          )}
          <div
            className="driver-signup-signature-toggle"
            style={{
              pointerEvents: userId ? "auto" : "none",
              opacity: userId ? 1 : 0.5,
            }}
          >
            <button
              type="button"
              className={`driver-signup-toggle-btn ${
                formData.signatureType === "upload" ? "active" : ""
              }`}
              onClick={() => handleSignatureTypeChange("upload")}
              disabled={!userId}
            >
              Upload Signature
            </button>
            <button
              type="button"
              className={`driver-signup-toggle-btn ${
                formData.signatureType === "draw" ? "active" : ""
              }`}
              onClick={() => handleSignatureTypeChange("draw")}
              disabled={!userId}
            >
              Draw Signature
            </button>
          </div>

          {formData.signatureType === "upload" && userId ? (
            <div className="driver-signup-file-input">
              <label
                htmlFor="signature-upload"
                className="driver-signup-file-label"
              >
                <div className="driver-signup-file-icon">
                  <i className="fas fa-cloud-upload-alt"></i>
                </div>
                <span className="driver-signup-file-text">
                  {formData.signature
                    ? `Selected: ${formData.signature.name}`
                    : "Click to upload signature (PNG/JPG)"}
                </span>
                <input
                  id="signature-upload"
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                className="driver-signup-canvas-btn"
                onClick={uploadFileSignature}
              >
                Upload Signature
              </button>
            </div>
          ) : formData.signatureType === "draw" && userId ? (
            <div className="driver-signup-canvas-container">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={endDrawing}
                onMouseLeave={endDrawing}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startDrawing(e.touches[0]);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  draw(e.touches[0]);
                }}
                onTouchEnd={endDrawing}
                className="driver-signup-canvas"
              />
              <div className="driver-signup-canvas-tools">
                <button
                  type="button"
                  onClick={clearCanvas}
                  className="driver-signup-canvas-btn"
                >
                  Clear Signature
                </button>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <label>Color:</label>
                  <input
                    type="color"
                    value={lineColor}
                    onChange={(e) => {
                      setLineColor(e.target.value);
                      const ctx = canvasRef.current.getContext("2d");
                      ctx.strokeStyle = e.target.value;
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <label>Width:</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={lineWidth}
                    onChange={(e) => {
                      setLineWidth(e.target.value);
                      const ctx = canvasRef.current.getContext("2d");
                      ctx.lineWidth = e.target.value;
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                className="driver-signup-canvas-btn"
                onClick={saveCanvasImage}
              >
                Upload Signature
              </button>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default Signup;
